import {
  EButtonType,
  IBrokerData,
  NewbuildingBrokerContext,
  NewbuildingBrokerModalWindow,
} from '@cian/frontend-newbuilding-broker-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { getCurrentRegionId } from '../../../../../../../shared/selectors/getCurrentRegionId';
import { getJsonQuery } from '../../../../../../../shared/selectors/getJsonQuery';
import { getIsNeedSelfEmployedConfirmation } from '../../../../../../../shared/selectors/newbuildingBroker';
import { getNewbuildingBrokerSettings } from '../../../../../../../shared/selectors/newbuildingBroker/getNewbuildingBrokerSettings';
import {
  trackErrorModalOpen,
  trackNextOrGoButtonClick,
  trackRetryButtonClick,
  trackRewardInstructionButtonClick,
  trackRewardInstructionLinkClick,
  trackSellApartmentClick,
} from '../../../../../../../shared/tracking/newbuilding/broker';
import { useApplicationContext } from '../../../../../../../shared/utils/applicationContext';
import { NewbuildingBrokerButton } from '../../../../components/NewbuildingBrokerButton';
import { useOfferContext } from '../../../../context';

export const NewbuildingBrokerButtonContainer: React.VFC = () => {
  const { logger, httpApi } = useApplicationContext();

  const brokerContextValue = React.useMemo(() => ({ httpApi, logger }), [httpApi, logger]);

  const {
    offer: { newbuilding, newbuildingBroker },
  } = useOfferContext();

  const newbuildingBrokerSettings = useSelector(getNewbuildingBrokerSettings);
  const regionId = useSelector(getCurrentRegionId);
  const needsSelfEmployedConfirmation = useSelector(getIsNeedSelfEmployedConfirmation);
  const jsonQuery = useSelector(getJsonQuery);

  const handleModalOpenerClick = React.useCallback(() => {
    trackSellApartmentClick(jsonQuery);
  }, []);

  const handleBrokerLinkFetched = React.useCallback((buttonType: EButtonType, result: IBrokerData | null) => {
    if (result) {
      trackNextOrGoButtonClick(jsonQuery, buttonType, result.brokerName);
    }
  }, []);

  const handleRewardInstructionButtonClick = React.useCallback(() => {
    trackRewardInstructionButtonClick(jsonQuery);
  }, []);

  const handleRewardInstructionLinkClick = React.useCallback(() => {
    trackRewardInstructionLinkClick(jsonQuery);
  }, []);

  const handleErrorModalOpen = React.useCallback(() => {
    trackErrorModalOpen(jsonQuery);
  }, []);

  const handleRetryButtonClick = React.useCallback(() => {
    trackRetryButtonClick(jsonQuery);
  }, []);

  const handleClick: React.MouseEventHandler = React.useCallback(event => event.stopPropagation(), []);

  const newbuildingId = Number(newbuilding?.id) || null;
  const houseId = Number(newbuilding?.house?.id) || null;
  const isBrokerAvailable = !!newbuildingBroker?.isAvailable;
  const isEnabledInConfig = newbuildingBrokerSettings?.enabled;
  const landingUrl = newbuildingBrokerSettings?.landingUrl;
  const personalDataAgreementLink = newbuildingBrokerSettings?.personalDataAgreement;

  if (!isEnabledInConfig || !isBrokerAvailable) {
    return null;
  }

  return (
    <NewbuildingBrokerContext.Provider value={brokerContextValue}>
      <div onClick={handleClick}>
        <NewbuildingBrokerModalWindow
          renderModalOpener={NewbuildingBrokerButton}
          newbuildingId={newbuildingId}
          newbuildingRegionId={regionId}
          houseId={houseId}
          isSelfEmployed={needsSelfEmployedConfirmation}
          landingUrl={landingUrl}
          personalDataAgreementLink={personalDataAgreementLink}
          onModalOpenerClick={handleModalOpenerClick}
          onBrokerLinkFetched={handleBrokerLinkFetched}
          onRewardInstructionButtonClick={handleRewardInstructionButtonClick}
          onRewardInstructionLinkClick={handleRewardInstructionLinkClick}
          onErrorModalOpen={handleErrorModalOpen}
          onRetryButtonClick={handleRetryButtonClick}
        />
      </div>
    </NewbuildingBrokerContext.Provider>
  );
};
